import queryKeys from '#hooks/queries/queryKeys';
import { useToast } from '#providers/ToastProvider';
import { useExercisePresetChartRecoil } from '#states/exercisePresetsState';
import { useSelectedExerciseListRecoil } from '#states/exerciseState';
import sessionStorageManager from '#utils/sessionStorageManager';
import { UseMutationOptions, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import TagManager from 'react-gtm-module';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import {
  IPostRecordsV2Params,
  usePatchRecordsDraft,
  usePostRecordsDraft,
  usePostRecordsV2
} from '../api';
import { useBeforeAfterPhotoState } from '../states/beforeAfterPhotoState';
import { useDashboardCharFormExerciseState } from '../states/dashboardCharFormExerciseState';
import { useDashboardChartFormPainPartsState } from '../states/dashboardChartFormPainPartsState';
import { useRecordImagesRecoil } from '../states/recordImagesState';
import { useRoundInfoState } from '../states/roundInfoState';
import { useTrainerFeedbackState } from '../states/trainerFeedbackState';
import { IDashBoardChartForm } from '../types';
import { isValidDashboardChartForm } from '../validate';
import { useMemberInfoFormRecoil } from '../states/memberInfoFormState';
import { useScheduleCoachDetailQuery } from '#hooks/queries/schedules/useScheduleCoachDetailQuery';
import { usePainPartsRecoil } from '../states/painPartsState';

export const SERACH_QUERY = {
  REGISTERED_LECTURE_ID: 'registered-lecture-id',
  PREVIOUS_REGISTERED_LECTURE_ID: 'previous-registered-lecture-id'
} as const;

/**
 * 운동차트 작성 프로세스 훅
 */
export const useDashboardChartFormProcess = () => {
  const [tab, setStateTab] = useState<'' | '운동 선택' | '프리셋 선택' | '폼'>(
    ''
  );

  const { setBeforeChartPath } = sessionStorageManager();

  const [searchParams] = useSearchParams();
  const searchQuery = {
    registeredLectureId: searchParams.get(SERACH_QUERY.REGISTERED_LECTURE_ID),
    previousRegisteredLectureId: searchParams.get(
      SERACH_QUERY.PREVIOUS_REGISTERED_LECTURE_ID
    )
  };

  const registeredLectureId = searchQuery.registeredLectureId as string;

  const { data: coachSchedule } = useScheduleCoachDetailQuery(
    {
      id: registeredLectureId as string
    },
    {
      enabled: !!registeredLectureId
    }
  );

  const { beforeChartPath, dashboardChartFormTab, setDashboardChartFormTab } =
    sessionStorageManager();

  useEffect(() => {
    setStateTab(dashboardChartFormTab);
  }, [dashboardChartFormTab]);

  const naviate = useNavigate();

  const { records, setRecords } = useDashboardCharFormExerciseState();

  const { roundInfo, setRoundInfo } = useRoundInfoState();

  const { trainerFeedback, setTrainerFeedback } = useTrainerFeedbackState();

  const { setDashboardChartFormPainParts } =
    useDashboardChartFormPainPartsState();

  const { beforeAfterPhoto, setBeforeAfterPhoto } = useBeforeAfterPhotoState();

  const { recordImages, setRecordImages } = useRecordImagesRecoil();

  // 운동차트 작성에 들어갈 프리셋 관리
  const { setExercisePresetChart } = useExercisePresetChartRecoil();

  // 선택된 운동 리스트
  const { setSelectedExerciseList } = useSelectedExerciseListRecoil();

  const queryClient = useQueryClient();

  const { showToast } = useToast();

  const { mutate: postRecordsDraft } = usePostRecordsDraft({
    onSuccess: () => {
      finish();
      showToast({
        type: 'success',
        message:
          '운동차트 작성 내용을 임시로 저장했습니다. \n 회원관리 > 운동차트 탭에서 이어서 작성해주세요!'
      });
    },
    onError: () => {
      showToast({
        type: 'fail',
        message: '운동차트 임시 저장에 실패했습니다.'
      });
    }
  });

  const { mutate: patchRecordsDraft } = usePatchRecordsDraft({
    onSuccess: () => {
      TagManager.dataLayer({
        dataLayer: {
          event: 'exercisechart_edit_complete',
          category: '회원관리',
          action: 'exercisechart_edit_complete',
          value: 2
        }
      });
      finish();
      showToast({
        type: 'success',
        message: '운동차트 작성 내용을 수정했습니다.'
      });
    },
    onError: () => {
      showToast({
        type: 'fail',
        message: '운동차트 수정에 실패했습니다.'
      });
    }
  });

  const { mutate: postRecordsV2 } = usePostRecordsV2();

  const reset = () => {
    setRecords([]);
    setRoundInfo(null);
    setTrainerFeedback('');
    setDashboardChartFormPainParts([]);
    setBeforeAfterPhoto([]);
    setRecordImages([]);
    setSelectedExerciseList([]);
    setExercisePresetChart([]);
  };

  // 임시저장, 수정, 발송 완료시 공통으로 사용
  // 추가적인 것은 각 컴포넌트에서 처리
  const finish = () => {
    // 성공시 사용한 모든 상태 초기화
    reset();

    // 운동차트 작성 전 페이지로 이동
    naviate(-1);

    // 운동 차트 작성 전 페이지에 따른 쿼리 무효화
    if (beforeChartPath?.includes('/detail')) {
      // 일정관리 상세 페이지
      queryClient.invalidateQueries([queryKeys.SCHEDULE, registeredLectureId]);
      setBeforeChartPath('');
    } else {
      // 고객관리 페이지
    }
  };
  const { memberInfoForm } = useMemberInfoFormRecoil();

  const params: IDashBoardChartForm = {
    comment: trainerFeedback,
    records,
    inbody: roundInfo,

    beforeAfterPhotoId: beforeAfterPhoto[0] || null,
    recordImages,
    preInformation: memberInfoForm,
    userId: coachSchedule?.personId || ''
  };

  const exit = () => {
    // 사용한 모든 상태 초기화
    reset();

    naviate(-1);
  };

  // 임시저장
  const save = () => {
    postRecordsDraft({
      registeredLectureId,
      params
    });
  };

  // 수정
  const edit = () => {
    patchRecordsDraft({
      registeredLectureId,
      params
    });
  };

  const { painParts } = usePainPartsRecoil();

  // 발송
  const send = (
    options?: UseMutationOptions<unknown, AxiosError, IPostRecordsV2Params>
  ) => {
    postRecordsV2(
      {
        registeredLectureId,
        params: {
          ...params,
          preInformation: {
            ...params.preInformation,
            healthInfo: {
              ...params.preInformation.healthInfo,
              painParts
            }
          }
        }
      },
      options
    );
  };

  const sendDisabled = !isValidDashboardChartForm(params);

  const setTab = (tab: '' | '운동 선택' | '프리셋 선택' | '폼') => {
    setDashboardChartFormTab(tab);
  };

  return {
    finish,
    save,
    send,
    sendDisabled,
    exit,
    tab,
    setTab,
    searchQuery,
    edit
  };
};
